import { api } from './api';

class IntelligentSearchService {
  // Autocomplete and Geocoding endpoints
  async getLocationSuggestions(input, types = '(regions)') {
    try {
      const response = await api.get(`/api/intelligent/autocomplete/?input=${encodeURIComponent(input)}&types=${types}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async geocodeLocation(locationQuery) {
    try {
      const response = await api.post('/api/intelligent/geocode/', {
        location_query: locationQuery
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Intelligent Search Areas Management
  async getIntelligentSearchAreas(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/api/intelligent/areas/?${queryParams}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async createIntelligentSearchArea(areaData) {
    try {
      const response = await api.post('/api/intelligent/areas/', areaData);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async getIntelligentSearchArea(areaId) {
    try {
      const response = await api.get(`/api/intelligent/areas/${areaId}/`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async updateIntelligentSearchArea(areaId, updateData) {
    try {
      const response = await api.patch(`/api/intelligent/areas/${areaId}/`, updateData);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async deleteIntelligentSearchArea(areaId) {
    try {
      const response = await api.delete(`/api/intelligent/areas/${areaId}/`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async regenerateCircles(areaId) {
    try {
      const response = await api.post(`/api/intelligent/areas/${areaId}/regenerate-circles/`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Circle Management and Search Execution
  async approveCircles(circleIds) {
    try {
      const response = await api.post('/api/intelligent/approve-circles/', {
        circle_ids: circleIds
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async executeIntelligentSearch(searchArea, circles) {
    try {
      const response = await api.post('/api/intelligent/execute-search/', {
        search_area: searchArea.id,
        circles: circles.map(circle => circle.id)
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Search Sessions Management
  async getIntelligentSearchSessions(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/api/intelligent/sessions/?${queryParams}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Statistics
  async getIntelligentSearchStats() {
    try {
      const response = await api.get('/api/intelligent/stats/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await api.get('/api/intelligent/health/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Utility methods
  formatConfidenceScore(score) {
    return `${(score * 100).toFixed(1)}%`;
  }

  formatRadius(radius) {
    if (radius >= 1000) {
      return `${(radius / 1000).toFixed(1)}km`;
    }
    return `${radius}m`;
  }

  getConfidenceColor(score) {
    if (score >= 0.8) return 'green';
    if (score >= 0.6) return 'yellow';
    if (score >= 0.4) return 'orange';
    return 'red';
  }

  getStatusColor(status) {
    switch (status) {
      case 'completed': return 'green';
      case 'ready': return 'blue';
      case 'processing': return 'yellow';
      case 'searching': return 'purple';
      case 'failed': return 'red';
      default: return 'gray';
    }
  }

  formatSearchAreaStatus(status) {
    switch (status) {
      case 'pending': return 'Pending Circle Generation';
      case 'processing': return 'Generating Circles';
      case 'ready': return 'Ready for Search';
      case 'searching': return 'Search in Progress';
      case 'completed': return 'Search Completed';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  calculateCircleStats(circles) {
    if (!circles || circles.length === 0) {
      return {
        total: 0,
        averageRadius: 0,
        averageConfidence: 0,
        highConfidence: 0,
        searched: 0
      };
    }

    const total = circles.length;
    const averageRadius = circles.reduce((sum, circle) => sum + circle.radius, 0) / total;
    const averageConfidence = circles.reduce((sum, circle) => sum + (circle.confidence_score || 0), 0) / total;
    const highConfidence = circles.filter(circle => (circle.confidence_score || 0) >= 0.8).length;
    const searched = circles.filter(circle => circle.has_been_searched).length;

    return {
      total,
      averageRadius: Math.round(averageRadius),
      averageConfidence,
      highConfidence,
      searched
    };
  }
}

export const intelligentSearchService = new IntelligentSearchService();
export default IntelligentSearchService;